/**
 * task-status-colors — Single source of truth for task status color palettes.
 *
 * Consumed by:
 * - TaskDependencyTab (Cytoscape nodes)
 * - TaskWorkflowMapTab (Cytoscape nodes)
 * - TaskScheduleTab (FullCalendar events)
 * - Legend components in all three tabs
 *
 * DUMB FRONTEND: Colors are visual-only. Status values from GqlTaskStatus enum.
 */

import { GqlTaskStatus } from '@/generated/graphql';

// ============================================================================
// STATUS → COLOR MAP
// ============================================================================

export interface StatusColor {
  /** Fill / background color */
  bg: string;
  /** Border / accent color */
  border: string;
}

/**
 * Canonical status → color mapping for task visualizations.
 * All Cytoscape graphs and FullCalendar events derive from this.
 */
export const TASK_STATUS_COLORS: Record<string, StatusColor> = {
  [GqlTaskStatus.COMPLETED]: { bg: '#51cf66', border: '#2f9e44' },
  [GqlTaskStatus.IN_PROGRESS]: { bg: '#4dabf7', border: '#1c7ed6' },
  [GqlTaskStatus.ASSIGNED]: { bg: '#22b8cf', border: '#15aabf' },
  [GqlTaskStatus.READY]: { bg: '#3bc9db', border: '#15aabf' },
  [GqlTaskStatus.PENDING]: { bg: '#ffd43b', border: '#fab005' },
  [GqlTaskStatus.BLOCKED]: { bg: '#ff922b', border: '#e8590c' },
  [GqlTaskStatus.PAUSED]: { bg: '#cc5de8', border: '#9c36b5' },
  [GqlTaskStatus.PENDING_EXTERNAL]: { bg: '#da77f2', border: '#9c36b5' },
  [GqlTaskStatus.FAILED]: { bg: '#ff6b6b', border: '#c92a2a' },
  [GqlTaskStatus.CANCELLED]: { bg: '#adb5bd', border: '#868e96' },
  [GqlTaskStatus.COMPENSATED]: { bg: '#868e96', border: '#495057' },
};

/** Fallback color when status is unknown */
export const DEFAULT_STATUS_COLOR: StatusColor = { bg: '#e9ecef', border: '#868e96' };

/** Get colors for a task status, with fallback */
export function getTaskStatusColor(status: string | undefined): StatusColor {
  return TASK_STATUS_COLORS[status ?? ''] ?? DEFAULT_STATUS_COLOR;
}

// ============================================================================
// LEGEND ENTRIES (derived from TASK_STATUS_COLORS)
// ============================================================================

export interface LegendEntry {
  color: string;
  label: string;
}

/** Standard legend entries for task status visualizations */
export const TASK_STATUS_LEGEND: LegendEntry[] = [
  { color: TASK_STATUS_COLORS[GqlTaskStatus.COMPLETED]!.bg, label: 'Completed' },
  { color: TASK_STATUS_COLORS[GqlTaskStatus.IN_PROGRESS]!.bg, label: 'In Progress' },
  { color: TASK_STATUS_COLORS[GqlTaskStatus.ASSIGNED]!.bg, label: 'Assigned' },
  { color: TASK_STATUS_COLORS[GqlTaskStatus.PENDING]!.bg, label: 'Pending' },
  { color: TASK_STATUS_COLORS[GqlTaskStatus.BLOCKED]!.bg, label: 'Blocked' },
  { color: TASK_STATUS_COLORS[GqlTaskStatus.FAILED]!.bg, label: 'Failed' },
];
